import React from 'react';
import { Star, StarHalf } from 'lucide-react';

/**
 * StarRating — read-only star display, supports half-stars.
 *
 * Props:
 *  - value: number (0–5, decimal allowed; e.g. 4.5)
 *  - size: number  (Tailwind h-N / w-N)  default 3
 *  - showCount: boolean — whether to render "(N)" count after stars
 *  - count: number — review count
 *  - className: string — additional classes on wrapper
 */
export const StarRating = ({ value = 0, size = 3, showCount = false, count = 0, className = '' }) => {
  const safe = Math.max(0, Math.min(5, Number(value) || 0));
  const rounded = Math.round(safe * 2) / 2; // nearest 0.5
  const full = Math.floor(rounded);
  const half = rounded - full === 0.5 ? 1 : 0;
  const empty = 5 - full - half;
  const cls = `h-${size} w-${size}`;

  return (
    <span
      className={`inline-flex items-center gap-0.5 ${className}`}
      aria-label={`Rating ${safe} out of 5`}
      data-testid='star-rating'
    >
      {Array.from({ length: full }).map((_, i) => (
        <Star key={`f${i}`} className={`${cls} fill-yellow-500 text-yellow-500`} />
      ))}
      {half === 1 && (
        <span className='relative inline-flex' aria-hidden='true'>
          <Star className={`${cls} text-yellow-500/50`} />
          <StarHalf className={`${cls} absolute left-0 top-0 fill-yellow-500 text-yellow-500`} />
        </span>
      )}
      {Array.from({ length: empty }).map((_, i) => (
        <Star key={`e${i}`} className={`${cls} text-muted-foreground/40`} />
      ))}
      {showCount && count > 0 && (
        <span className='ml-1 text-[11px] text-muted-foreground tabular-nums'>({count})</span>
      )}
    </span>
  );
};

export default StarRating;
